import React, { useEffect, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";
import Accordion from 'react-bootstrap/Accordion';
import { Col, Row } from "react-bootstrap";
import { ICheckoutDetails } from "interfaces/CheckoutDetailsProps";
import { Item } from "types/api.types";
import CONSTANTS from "../../utils/constants";
import shoppingBag from "../../assets/img/shopping-bag.svg";
import styles from "./checkoutdetails.module.css";
import "./accordion.css";
import { currencyToSymbol, formatAmount } from "../../utils/utils";
import { ChevronDown } from "react-bootstrap-icons";
import { useTheme } from "../../context/ThemeContext";

const OrderItem: React.FC<{
  item: Item;
  currencyCode: string;
}> = ({ item, currencyCode }) => {
  return (
    <Row className={`${styles.orderItem}` + " d-flex"}>
      <Col
        className={
          `${styles.orderItemIcon}` +
          " d-flex align-items-center align-self-center"
        }
      >
        {/* change src with actual img from api response */}
        <img
          src={shoppingBag}
          alt={item.invoiceData.description}
          className={`${styles.orderItemImg}`}
          data-testid="orderItem-icon"
        />
      </Col>
      <Col className="d-flex flex-column align-self-center">
        <p className="volte-semibold item-description" data-testid="orderItem-name">
          {item.invoiceData.description}
        </p>
        <p className="volte-medium" data-testid="orderItem-quantity">
          <FormattedMessage
            id={CONSTANTS.CHECKOUT_DETAILS.QUANTITY}
            defaultMessage="Quantity"
          />
          : {item.orderLineDetails.quantity}
        </p>
      </Col>
      <Col className="d-flex align-items-start justify-content-end p-0">
        <p className="volte-semibold" data-testid="orderItem-price">
          {formatAmount(item.orderLineDetails.productPrice)} {currencyCode}
        </p>
      </Col>
    </Row>
  );
};

const CheckoutDetails: React.FC<{ CheckoutDetailsData: ICheckoutDetails | null }> = ({ CheckoutDetailsData }) => {

  // setting up the states to handle diff product types
  const [totalDiscountPrice, setTotalDiscountPrice] = useState<string>("");
  const [totalHandlingFee, setHandlingFee] = useState<string>("");
  const [totalShipmentCost, setTotalShipmentCost] = useState<string>("");

  const orderSummaryElementtRef = useRef(null);
  const orderSummaryWrapElementtRef = useRef(null);
  const [activeKey, setActiveKey] = useState<string | null>("0");
  const [hasUserExpandedAccordion, setHasUserExpandedAccordion] = useState(false);
  const [isAccordionFloating, setIsAccordionFloating] = useState(false);
  const [currrentScrollPosition, setCurrrentScrollPosition] = useState(0);

  const calculateTax = () => {
    let totalTax = 0;
    CheckoutDetailsData?.items.forEach((item) => {
      totalTax += item.orderLineDetails.taxAmount
        ? Number(item.orderLineDetails.taxAmount)
        : 0;
    });
    return totalTax;
  };

  const currencySymbol = currencyToSymbol(CheckoutDetailsData?.amountOfMoney.currencyCode);
  const { theme } = useTheme();

  useEffect(() => {

    const initialValues = {
      discountTotal: 0,
      handlingFeeTotal: 0,
      shipmentCost: 0,
    };

    const computedValues =
      CheckoutDetailsData?.items.reduce(
        (
          acc: {
            discountTotal: number;
            handlingFeeTotal: number;
            shipmentCost: number;
          },
          item
        ) => {
          const { productPrice, productType, quantity } = item.orderLineDetails;
          if (productType === "DISCOUNT") {
            acc.discountTotal += productPrice * quantity;
          }
          if (productType === "HANDLING_FEE") {
            acc.handlingFeeTotal += productPrice * quantity;
          }
          if (productType === "SHIPMENT") {
            acc.shipmentCost += productPrice * quantity;
          }
          return acc;
        },
        initialValues
      ) || initialValues;

    const { discountTotal, handlingFeeTotal, shipmentCost } = computedValues;

    if (discountTotal > 0) {
      const formattedDiscount = formatAmount(discountTotal);
      setTotalDiscountPrice(formattedDiscount.toString());
    }

    if (handlingFeeTotal > 0) {
      const formattedHandlingFee = formatAmount(handlingFeeTotal);
      setHandlingFee(formattedHandlingFee.toString());
    }

    if (shipmentCost > 0) {
      const formattedShipmentConst = formatAmount(shipmentCost);
      setTotalShipmentCost(formattedShipmentConst.toString());
    }

    const handleScroll = () => {
      setCurrrentScrollPosition(window.scrollY);
    }

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);

  }, []);

  //to close the accordion when user clicks outside the accordion on mobile and tablet screen sizes
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isDeviceMobileOrTablet()) {
        const target = event.target as Node;

        const orderSummaryElement = orderSummaryElementtRef.current as unknown as HTMLDivElement;
        // Check if click is within floating element
        if (orderSummaryElement && orderSummaryElement.contains(target)) {
          return; // Don't close - user clicked inside floating UI
        }
        else if (isAccordionFloating) {
          setActiveKey(null);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);

  }, [isAccordionFloating]);

  useEffect(() => {
    toggleAccordionFloating();
  }, [currrentScrollPosition]);

  const toggleAccordionFloating = () => {
      if (isDeviceMobileOrTablet() && currrentScrollPosition > 540 && !hasUserExpandedAccordion && !isAccordionFloating) {
        setActiveKey(null);
        setIsAccordionFloating(true);

        window.scrollTo({
          top: 110,
          left: 0,
          behavior: "instant",
        });
      }

      if (currrentScrollPosition < 105 && isAccordionFloating) {
        setActiveKey("0");
        setIsAccordionFloating(false);
        setHasUserExpandedAccordion(false);
      }
  }

  const isDeviceMobileOrTablet = () => {
    return window.matchMedia('(min-width: 768px) and (max-width: 991px)').matches || window.matchMedia('(max-width: 767px)').matches;
  }

  return (
    <>
      <div className={`${styles.orderContainer}`}>
        <h4
          className="volte-semibold order-summary-header"
          data-testid="orderSummary-header"
        >
          <FormattedMessage
            id={CONSTANTS.CHECKOUT_DETAILS.SECTION_HEADING}
            defaultMessage="Order summary"
          />
        </h4>

        {CheckoutDetailsData ? (
          <>
            <div className={`${styles.orderItems}` + " m-0"}>
              {CheckoutDetailsData.items.filter(item => item.orderLineDetails.productType === 'GOODS').map((item, index) => (
                <OrderItem
                  key={item.invoiceData.description + index}
                  item={item}
                  currencyCode={currencySymbol}
                />
              ))}
            </div>

            <div
              className={`${styles.orderTotal}` + " m-0 p-0"}
              data-testid="orderSummary-total"
            >
              <span
                className="rounded"
                style={{
                  backgroundColor: "#666",
                  height: "1px",
                  marginTop: "8px",
                }}
              />
              <div className="d-flex justify-content-between">
                <p
                  className="volte-medium"
                  data-testid="checkoutDetails-taxAmount-text"
                >
                  <FormattedMessage
                    id={CONSTANTS.CHECKOUT_DETAILS.TAX_AMOUNT}
                    defaultMessage="Tax amount"
                  />
                </p>
                <p
                  className="volte-medium"
                  data-testid="checkoutDetails-taxAmount-price"
                >
                  {formatAmount(calculateTax())} {currencySymbol}
                </p>
              </div>
              {totalShipmentCost !== "" && (
                <div className="d-flex justify-content-between">
                  <p
                    className="volte-medium"
                    data-testid="checkoutDetails-shippingCost-text"
                  >
                    <FormattedMessage
                      id={CONSTANTS.CHECKOUT_DETAILS.SHIPPING_COST}
                      defaultMessage="Shipping Cost"
                    />
                  </p>

                  <p
                    className="volte-medium"
                    data-testid="checkoutDetails-shippingCost-price"
                  >
                    {totalShipmentCost} {currencySymbol}
                  </p>
                </div>
              )}
              {totalHandlingFee !== "" && (
                <div className="d-flex justify-content-between">
                  <p
                    className="volte-medium"
                    data-testid="checkoutDetails-shippingCost-text"
                  >
                    <FormattedMessage
                      id={CONSTANTS.CHECKOUT_DETAILS.HANDLING_FEE}
                      defaultMessage="Handling fee"
                    />
                  </p>

                  <p
                    className="volte-medium"
                    data-testid="checkoutDetails-shippingCost-price"
                  >
                    {totalHandlingFee} {currencySymbol}
                  </p>
                </div>
              )}
              {totalDiscountPrice !== "" && (
                <div className="d-flex justify-content-between">
                  <p
                    className="volte-medium"
                    data-testid="checkoutDetails-shippingCost-text"
                  >
                    <FormattedMessage
                      id={CONSTANTS.CHECKOUT_DETAILS.DISCOUNT}
                      defaultMessage="Discount"
                    />
                  </p>

                  <p
                    className="volte-medium"
                    data-testid="checkoutDetails-shippingCost-price"
                  >
                    - {totalDiscountPrice} {currencySymbol}
                  </p>
                </div>
              )}
              <div
                className={
                  `${styles.totalAmount}` + " d-flex justify-content-between"
                }
              >
                <p
                  className="volte-semibold"
                  style={{ color: "#0096D6" }}
                  data-testid="checkoutDetails-totalAmount-text"
                >
                  <FormattedMessage
                    id={CONSTANTS.CHECKOUT_DETAILS.TOTAL_AMOUNT}
                    defaultMessage="Total amount"
                  />
                </p>
                <p
                  className="volte-semibold"
                  style={{ color: "#0096D6" }}
                  data-testid="checkoutDetails-totalAmount-price"
                >
                  {formatAmount(CheckoutDetailsData.amountOfMoney.amount)}{" "}
                  {currencySymbol}
                </p>
              </div>
              <span
                className="rounded"
                style={{ backgroundColor: "#666", height: ".5px" }}
              />
            </div>
          </>
        ) : (
          <div>
            <b>
              <FormattedMessage
                id={CONSTANTS.NO_DATA_AVAILABLE}
                defaultMessage="No Data Available"
              />
            </b>
          </div>
        )}
      </div>

      <div ref={orderSummaryWrapElementtRef} id="checkoutDetails-accordion" className={isAccordionFloating ? "checkoutDetails-accordion fixed-accordion collapse col-lg-6 col-12" : "checkoutDetails-accordion col-lg-6 col-12"}>
        <Accordion
          ref={orderSummaryElementtRef}
          className={(activeKey ? "mainAccordion-expand" : "mainAccordion-collapse") + (isAccordionFloating ? " accordion-floating" : " accordion-not-floating")}
          defaultActiveKey="0"
          activeKey={activeKey}
          onSelect={(key) => {
            setHasUserExpandedAccordion(isAccordionFloating);
            setActiveKey(key as string | null);
            setActiveKey(isAccordionFloating ? key as string | null : "0");
          }}
          flush={isAccordionFloating ? false : true}
        >
          <Accordion.Item eventKey="0" className={activeKey ? "accordion-item-expand" : "accordion-item-collapse"}>
            <Accordion.Header className={!isAccordionFloating ? "accordion-header-button" : ""} >
              <>
                <h4
                  className="volte-semibold order-summary-header"
                  data-testid="orderSummary-header"
                  style={{ color: 'var(--text-color)' }}
                >
                  <FormattedMessage
                    id={CONSTANTS.CHECKOUT_DETAILS.SECTION_HEADING}
                    defaultMessage="Order summary"
                  />
                </h4>
                {CheckoutDetailsData && isAccordionFloating && <div
                  className={
                    `${styles.totalAmount} ${activeKey ? "hidden" : ""}` + " totalAmount d-flex justify-content-between align-items-center"
                  }
                >
                  <p
                    className="volte-semibold"
                    style={{ color: "#0096D6" }}
                    data-testid="checkoutDetails-totalAmount-text"
                  >
                    <FormattedMessage
                      id={CONSTANTS.CHECKOUT_DETAILS.TOTAL_AMOUNT}
                      defaultMessage="Total amount"
                    />
                  </p>
                  <p
                    className="volte-semibold"
                    style={{ color: "#0096D6" }}
                  >
                    :{" "}{formatAmount(CheckoutDetailsData.amountOfMoney.amount)}{" "}{currencySymbol}
                  </p>
                </div>
                }
                {CheckoutDetailsData && isAccordionFloating && theme === "dark" &&
                  <ChevronDown
                    size={20}
                    className={`${styles.chevronIcon} ${activeKey === "0" ? styles.rotated : styles.normal}`}
                  />
                }
              </>
           
            </Accordion.Header>
            <Accordion.Body>
              {CheckoutDetailsData ? (
                <>
                  <div className={`${styles.orderItems} orderItems` + " m-0"}>
                    {CheckoutDetailsData.items.filter(item => item.orderLineDetails.productType === 'GOODS').map((item, index) => (
                      <OrderItem
                        key={item.invoiceData.description + index}
                        item={item}
                        currencyCode={currencySymbol}
                      />
                    ))}
                  </div>

                  <div
                    className={`${styles.orderTotal}` + " orderSummary-total m-0 p-0"}
                    data-testid="orderSummary-total"
                  >
                    <span
                      className="rounded"
                      style={{
                        backgroundColor: "#666",
                        height: "1px",
                        marginTop: "8px",
                      }}
                    />
                    <div className="d-flex justify-content-between">
                      <p
                        className="volte-medium"
                        data-testid="checkoutDetails-taxAmount-text"
                      >
                        <FormattedMessage
                          id={CONSTANTS.CHECKOUT_DETAILS.TAX_AMOUNT}
                          defaultMessage="Tax amount"
                        />
                      </p>
                      <p
                        className="volte-medium"
                        data-testid="checkoutDetails-taxAmount-price"
                      >
                        {formatAmount(calculateTax())} {currencySymbol}
                      </p>
                    </div>
                    {totalShipmentCost !== "" && (
                      <div className="d-flex justify-content-between">
                        <p
                          className="volte-medium"
                          data-testid="checkoutDetails-shippingCost-text"
                        >
                          <FormattedMessage
                            id={CONSTANTS.CHECKOUT_DETAILS.SHIPPING_COST}
                            defaultMessage="Shipping Cost"
                          />
                        </p>

                        <p
                          className="volte-medium"
                          data-testid="checkoutDetails-shippingCost-price"
                        >
                          {totalShipmentCost} {currencySymbol}
                        </p>
                      </div>
                    )}
                    {totalHandlingFee !== "" && (
                      <div className="d-flex justify-content-between">
                        <p
                          className="volte-medium"
                          data-testid="checkoutDetails-shippingCost-text"
                        >
                          <FormattedMessage
                            id={CONSTANTS.CHECKOUT_DETAILS.HANDLING_FEE}
                            defaultMessage="Handling fee"
                          />
                        </p>

                        <p
                          className="volte-medium"
                          data-testid="checkoutDetails-shippingCost-price"
                        >
                          {totalHandlingFee} {currencySymbol}
                        </p>
                      </div>
                    )}
                    {totalDiscountPrice !== "" && (
                      <div className="d-flex justify-content-between">
                        <p
                          className="volte-medium"
                          data-testid="checkoutDetails-shippingCost-text"
                        >
                          <FormattedMessage
                            id={CONSTANTS.CHECKOUT_DETAILS.DISCOUNT}
                            defaultMessage="Discount"
                          />
                        </p>

                        <p
                          className="volte-medium"
                          data-testid="checkoutDetails-shippingCost-price"
                        >
                          - {totalDiscountPrice} {currencySymbol}
                        </p>
                      </div>
                    )}
                    <div
                      className={
                        `${styles.totalAmount}` + " d-flex justify-content-between"
                      }
                    >
                      <p
                        className="volte-semibold"
                        style={{ color: "#0096D6" }}
                        data-testid="checkoutDetails-totalAmount-text"
                      >
                        <FormattedMessage
                          id={CONSTANTS.CHECKOUT_DETAILS.TOTAL_AMOUNT}
                          defaultMessage="Total amount"
                        />
                      </p>
                      <p
                        className="volte-semibold"
                        style={{ color: "#0096D6" }}
                      >
                        {formatAmount(CheckoutDetailsData.amountOfMoney.amount)}{" "}
                        {currencySymbol}
                      </p>
                    </div>
                  </div>
                </>
              ) : (
                <div>
                  <b>
                    <FormattedMessage
                      id={CONSTANTS.NO_DATA_AVAILABLE}
                      defaultMessage="No Data Available"
                    />
                  </b>
                </div>
              )}
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>
      </div>
    </>
  );
};

export default React.memo(CheckoutDetails);